import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type Theme = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export interface ThemeContextValue {
  /** Preferência do usuário (pode ser 'system') */
  theme: Theme;
  /** Tema efetivamente aplicado no DOM ('light' ou 'dark', resolvido) */
  resolvedTheme: ResolvedTheme;
  /** Muda o tema e persiste em localStorage */
  setTheme: (theme: Theme) => void;
  /** Atalho: alterna entre light e dark (ignora 'system') */
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/** Chave de armazenamento — também usada pelo script inline de FOUC */
export const THEME_STORAGE_KEY = 'kmgestao.theme';

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyThemeToDOM(resolved: ResolvedTheme): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', resolved);
  // Atualiza também <meta name="theme-color"> para status bar iOS/Android
  const themeColor = resolved === 'dark' ? '#0a1628' : '#ffffff';
  let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', 'theme-color');
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', themeColor);
}

function readStoredTheme(defaultTheme: Theme): Theme {
  if (typeof window === 'undefined') return defaultTheme;
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored;
    }
  } catch {
    // localStorage pode não estar disponível (modo privado, iframe restrito)
  }
  return defaultTheme;
}

export interface ThemeProviderProps {
  children: ReactNode;
  /** Tema inicial se o usuário ainda não escolheu. Default: 'system' */
  defaultTheme?: Theme;
}

export function ThemeProvider({ children, defaultTheme = 'system' }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() => readStoredTheme(defaultTheme));
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() =>
    theme === 'system' ? getSystemTheme() : theme,
  );

  // Aplica tema no DOM sempre que mudar + persiste preferência
  useEffect(() => {
    const next = theme === 'system' ? getSystemTheme() : theme;
    setResolvedTheme(next);
    applyThemeToDOM(next);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // ignora falha de persistência
    }
  }, [theme]);

  // Escuta mudança de prefers-color-scheme quando theme = 'system'
  useEffect(() => {
    if (theme !== 'system') return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (): void => {
      const next = getSystemTheme();
      setResolvedTheme(next);
      applyThemeToDOM(next);
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((current) => {
      const currentResolved = current === 'system' ? getSystemTheme() : current;
      return currentResolved === 'dark' ? 'light' : 'dark';
    });
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, resolvedTheme, setTheme, toggleTheme }),
    [theme, resolvedTheme, setTheme, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/**
 * Hook para acessar o tema atual e trocá-lo.
 * Deve ser usado dentro de um <ThemeProvider>.
 */
export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme() deve ser chamado dentro de <ThemeProvider>.');
  }
  return ctx;
}

/**
 * Script inline a ser colado em <head> do index.html ANTES do bundle React.
 * Aplica o tema persistido imediatamente para evitar FOUC (flash of
 * unstyled content) entre carga da página e hidratação React.
 *
 * Uso em Vite:
 *   <script>%KMGESTAO_THEME_SCRIPT%</script>
 * (Substitua o placeholder no build ou cole a string diretamente.)
 */
export const FOUC_PREVENTION_SCRIPT = `(function(){try{var t=localStorage.getItem('${THEME_STORAGE_KEY}')||'light';var d=t==='system'?(window.matchMedia('(prefers-color-scheme: dark)').matches?'dark':'light'):t;document.documentElement.setAttribute('data-theme',d);}catch(e){}})();`;
